/**
 * 속닥속닥 글쓰기 제한(도배 방지) — 1시간에 글 5개 이상 작성 시 24시간 제한.
 *
 * 서버가 내려주는 상태를 그대로 담고, 남은 시간 문구만 화면용으로 만든다.
 * 기준값(5개/1시간/24시간)은 서버 설정이라 응답 값을 그대로 쓴다.
 */
export interface PostWriteLimit {
    blocked: boolean;
    /** 제한 해제 시각 (ISO, 제한 중일 때만) */
    blockedUntil?: string | null;
    /** 해제까지 남은 초 (제한 중일 때만) */
    remainingSeconds?: number | null;
    maxPosts: number;
    windowMinutes: number;
    blockHours: number;
    /** 서버가 만든 안내 문구 (제한 중일 때만) */
    message?: string | null;
}

/** 남은 시간 문구 — 1시간 이상이면 "23시간 12분", 1시간 미만이면 "42분", 1분 미만이면 "잠시 후". */
export const formatRemaining = (seconds: number): string => {
    if (seconds < 60) {
        return '잠시 후';
    }
    const totalMinutes: number = Math.floor(seconds / 60);
    const hours: number = Math.floor(totalMinutes / 60);
    const minutes: number = totalMinutes % 60;
    if (hours <= 0) {
        return `${minutes}분`;
    }
    return minutes > 0 ? `${hours}시간 ${minutes}분` : `${hours}시간`;
};

/** 해제 시각 문구 — "9월 24일 01:13". 값이 없으면 빈 문자열. */
export const formatBlockedUntil = (blockedUntil?: string | null): string => {
    if (!blockedUntil) {
        return '';
    }
    const date: Date = new Date(blockedUntil);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const month: number = date.getMonth() + 1;
    const day: number = date.getDate();
    const hh: string = String(date.getHours()).padStart(2, '0');
    const mm: string = String(date.getMinutes()).padStart(2, '0');
    return `${month}월 ${day}일 ${hh}:${mm}`;
};

/** 제한 사유 문구 — "어뷰징 방지를 위해 1시간에 글을 5개 이상 쓰면 24시간 동안 글쓰기가 제한돼요." */
export const describeLimitRule = (limit: PostWriteLimit): string => {
    const window: string = limit.windowMinutes % 60 === 0 ? `${limit.windowMinutes / 60}시간` : `${limit.windowMinutes}분`;
    return `어뷰징 방지를 위해 ${window}에 글을 ${limit.maxPosts}개 이상 쓰면 ${limit.blockHours}시간 동안 글쓰기가 제한돼요.`;
};

/**
 * 해제 시각 기준으로 남은 초를 다시 계산한다.
 * 모달이 열려 있는 동안 1분마다 갱신할 때 쓴다(서버 응답 시점 이후 흐른 시간 반영).
 */
export const remainingSecondsFrom = (limit: PostWriteLimit, now: Date = new Date()): number => {
    if (!limit.blockedUntil) {
        return Math.max(limit.remainingSeconds ?? 0, 0);
    }
    const until: Date = new Date(limit.blockedUntil);
    if (Number.isNaN(until.getTime())) {
        return Math.max(limit.remainingSeconds ?? 0, 0);
    }
    return Math.max(Math.floor((until.getTime() - now.getTime()) / 1000), 0);
};
